/**
 * DSCR Rate Center — server functions.
 *
 * `getDscrRateSnapshot`  — public, read-only. Serves the stored anonymized
 *                          snapshot so the page renders real pricing on the
 *                          server without any client JavaScript.
 * `priceDscrScenario`    — public, validated, rate-limited, short-cached.
 *                          Calls Optimal Blue server-side and returns ONLY the
 *                          anonymized public projection.
 * `runStaffDscrQuote`    — authenticated staff service for CRM quoting.
 *                          Returns the full internal normalized quotes and
 *                          persists the run to the pricing tables.
 *
 * No provider credentials, bearer tokens, searchIds, product IDs, investor IDs
 * or raw payloads are ever returned from the public handlers.
 */

import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  aggregateFeeStatus,
  DEFAULT_RATE_SCENARIO,
  emptyRateResponse,
  hydratePublicOption,
  scenarioKey,
  scenarioLtv,
  type PublicRateResponse,
  type PublicRateScenario,
} from "./rate-center";
import { checkStateAvailability, gateDscrScenario } from "./rate-availability";
import { normalizeLocation } from "./state-location";
import { summarizeHistoryPoint } from "./rate-history";
import { RATE_DISCLOSURE_VERSION } from "./rate-disclosures";
import { assertInvestmentOccupancy } from "./business-purpose-guard";
import { projectPublicOption, projectPublicRateResponse } from "./public-rate-projection";

import {
  classifyFreshness,
  FRESHNESS_CONFIG,
  marketLikelyClosed,
  MARKET_CLOSED_NOTE,
  usableAsFallback,
  nextRatesChangedAt,
  pricingFingerprint,
} from "./rate-freshness";

const scenarioSchema = z.object({
  loanPurpose: z.enum(["purchase", "rate_term_refinance", "cash_out_refinance"]).default("purchase"),
  state: z.string().regex(/^[A-Z]{2}$/).default("FL"),
  city: z.string().max(60).default(""),
  county: z.string().max(60).default(""),
  zipCode: z.string().regex(/^\d{5}$/).default("33301"),
  propertyValue: z.number().min(50_000).max(10_000_000),
  loanAmount: z.number().min(50_000).max(7_500_000),
  fico: z.number().int().min(600).max(850),
  dscr: z.number().min(0.5).max(3),
  propertyType: z.enum(["SingleFamily", "Condo", "Townhouse", "PUD"]).default("SingleFamily"),
  units: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]).default(1),
  loanTerm: z.enum(["ThirtyYear", "FortyYear", "FifteenYear"]).default("ThirtyYear"),
  amortizationType: z.enum(["Fixed", "ARM"]).default("Fixed"),
  interestOnly: z.boolean().default(false),
  lockPeriodDays: z.union([z.literal(15), z.literal(30), z.literal(45), z.literal(60)]).default(30),
  prepaymentPenalty: z
    .enum(["None", "OneYear", "TwoYear", "ThreeYear", "FourYear", "FiveYear"])
    .default("None"),
  shortTermRental: z.boolean().default(false),
  entityVesting: z.boolean().default(true),
  // Exact Optimal Blue Citizenship enum — verified live against the API.
  citizenship: z
    .enum(["USCitizen", "PermResidentAlien", "NonPermResidentAlien", "ForeignNational"])
    .default("USCitizen"),
  hasITIN: z.boolean().default(false),
});

const PURPOSE_MAP = {
  purchase: "Purchase",
  rate_term_refinance: "RefiRateTermLimitedCO",
  cash_out_refinance: "RefiCashout",
} as const;

const UNIT_MAP = { 1: "OneUnit", 2: "TwoUnits", 3: "ThreeUnits", 4: "FourUnits" } as const;

const TERM_LABEL = { ThirtyYear: "30-Year", FortyYear: "40-Year", FifteenYear: "15-Year" } as const;

const UNAVAILABLE_COPY =
  "Live pricing is temporarily unavailable. Request a written quote and a loan officer will price your scenario.";

function fallbackProgramLabel(s: PublicRateScenario): string {
  const amort = s.amortizationType === "ARM" ? "ARM" : "Fixed";
  const io = s.interestOnly ? " Interest-Only" : "";
  return `${TERM_LABEL[s.loanTerm]} ${amort}${io} DSCR`;
}

/**
 * Attribute chips that are true for the whole search, taken straight from the
 * scenario the borrower entered. Nothing is inferred.
 */
function scenarioTags(s: PublicRateScenario): string[] {
  const tags = ["DSCR"];
  if (s.citizenship === "ForeignNational") tags.push("Foreign National");
  if (s.hasITIN) tags.push("ITIN");
  if (s.shortTermRental) tags.push("Short-Term Rental");
  if (s.entityVesting) tags.push("Entity Vesting");
  return tags;
}

function newCorrelationId(): string {
  try {
    return crypto.randomUUID().slice(0, 8);
  } catch {
    return Math.random().toString(36).slice(2, 10);
  }
}

/** Builds the provider scenario. Single place, so public and staff stay identical. */
function toProviderScenario(scenario: PublicRateScenario) {
  // Guarantee the state, ZIP, city and county agree before the request leaves:
  // Optimal Blue 400s when a county/city belongs to a different state.
  const loc = normalizeLocation({
    state: scenario.state,
    zipCode: scenario.zipCode,
    city: scenario.city,
    county: scenario.county,
  });
  return {
    loanPurpose: PURPOSE_MAP[scenario.loanPurpose],
    state: loc.state,
    city: loc.city || undefined,
    county: loc.county || undefined,
    zipCode: loc.zipCode || undefined,
    propertyType: scenario.propertyType,
    numberOfUnits: UNIT_MAP[scenario.units],
    appraisedValue: scenario.propertyValue,
    salesPrice: scenario.propertyValue,
    baseLoanAmount: scenario.loanAmount,
    fico: scenario.fico,
    dscr: scenario.dscr,
    loanTerm: scenario.loanTerm,
    amortizationType: scenario.amortizationType,
    interestOnly: scenario.interestOnly,
    lockPeriodDays: scenario.lockPeriodDays,
    prepaymentPenalty: scenario.prepaymentPenalty,
    shortTermRental: scenario.shortTermRental,
    entityVesting: scenario.entityVesting,
    // Passed straight through — never forced to USCitizen.
    citizenship: scenario.citizenship,
    hasITIN: scenario.hasITIN,
    monthsReserves: 6,
  };
}

// ---------------------------------------------------------------------------
// Abuse protection + short cache (per worker instance)
// ---------------------------------------------------------------------------

const RATE_LIMIT_WINDOW_MS = 60_000;
const RATE_LIMIT_MAX = 12;
/** Global ceiling so a distributed burst cannot run up provider cost. */
const GLOBAL_LIMIT_WINDOW_MS = 60_000;
const GLOBAL_LIMIT_MAX = 120;

const hits = new Map<string, number[]>();
let globalHits: number[] = [];
const cache = new Map<string, { at: number; payload: PublicRateResponse }>();
/** Collapses identical concurrent scenarios into one provider call. */
const inFlight = new Map<string, Promise<PublicRateResponse>>();

function rateLimited(ip: string): boolean {
  const now = Date.now();
  const recent = (hits.get(ip) ?? []).filter((t) => now - t < RATE_LIMIT_WINDOW_MS);
  recent.push(now);
  hits.set(ip, recent);
  if (hits.size > 500) {
    for (const [k, v] of hits) if (!v.some((t) => now - t < RATE_LIMIT_WINDOW_MS)) hits.delete(k);
  }
  return recent.length > RATE_LIMIT_MAX;
}

function globalLimited(): boolean {
  const now = Date.now();
  globalHits = globalHits.filter((t) => now - t < GLOBAL_LIMIT_WINDOW_MS);
  globalHits.push(now);
  return globalHits.length > GLOBAL_LIMIT_MAX;
}

function clientIp(): string {
  return (
    getRequestHeader("cf-connecting-ip") ??
    getRequestHeader("x-forwarded-for")?.split(",")[0]?.trim() ??
    "unknown"
  );
}

// ---------------------------------------------------------------------------
// Snapshot (public, anonymized)
// ---------------------------------------------------------------------------

async function readSnapshot(
  key: string,
  scenarioFallback: PublicRateScenario,
): Promise<PublicRateResponse | null> {
  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("public_rate_snapshots")
      .select("scenario, options, as_of, rates_changed_at" as "scenario, options, as_of")
      .eq("scenario_key", key)
      .maybeSingle();
    if (!data) return null;
    const scenario = (data.scenario as unknown as PublicRateScenario) ?? scenarioFallback;
    // Hydrate first (older rows need the internal fee key to rebuild the
    // primary fee), then project: stored rows never leave the server raw.
    const options = (((data.options as unknown as PublicRateResponse["options"]) ?? []) as PublicRateResponse["options"])
      .map(hydratePublicOption)
      .map(projectPublicOption);
    const freshness = classifyFreshness(data.as_of, "snapshot");
    if (!freshness.displayable) return null;
    // A displayed table is always one lock period. Snapshots can hold a ladder,
    // so filter to the requested lock and report what the snapshot can offer.
    const snapshotLocks = [...new Set(options.map((o) => o.lockPeriod).filter((d): d is number => !!d))].sort(
      (a, b) => a - b,
    );
    const wantedLock = scenarioFallback.lockPeriodDays;
    const lockedOptions = snapshotLocks.includes(wantedLock)
      ? options.filter((o) => o.lockPeriod === wantedLock)
      : options;
    return {
      ok: true,
      source: "snapshot",
      asOf: data.as_of,
      ratesChangedAt: (data as { rates_changed_at?: string | null }).rates_changed_at ?? null,
      scenario,
      ltv: scenarioLtv(scenario),
      options: lockedOptions,
      providerParQuotes: [],
      availableLockPeriods: snapshotLocks,
      message: null,
      freshness,
      availability: checkStateAvailability(scenario.state),
      feeDataStatus: aggregateFeeStatus(options),
      correlationId: null,
      notices: freshness.requiresStaleNotice ? [freshness.label] : [],
    };
  } catch {
    return null;
  }
}

async function writeSnapshot(key: string, payload: PublicRateResponse): Promise<void> {
  // Never stamp a snapshot with write time: no real priced-at time → no snapshot.
  if (!payload.asOf || !payload.ok || payload.options.length === 0) return;
  const asOf = payload.asOf;
  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // Real-change detection: verification moves as_of; only different pricing moves rates_changed_at.
    const { data: prev } = await supabaseAdmin
      .from("public_rate_snapshots")
      .select("options, rates_changed_at" as "options")
      .eq("scenario_key", key)
      .maybeSingle();
    const prevRow = prev as { options?: unknown; rates_changed_at?: string | null } | null;
    const ratesChangedAt = nextRatesChangedAt(
      prevRow
        ? {
            fingerprint: pricingFingerprint((prevRow.options as PublicRateResponse["options"]) ?? []),
            changedAt: prevRow.rates_changed_at ?? null,
          }
        : null,
      pricingFingerprint(payload.options),
      asOf,
    );
    await supabaseAdmin.from("public_rate_snapshots").upsert(
      {
        rates_changed_at: ratesChangedAt,
        scenario_key: key,
        product_slug: "dscr",
        // Only the consumer-safe scenario and anonymized options are stored.
        scenario: payload.scenario as never,
        // Stored already projected: no lender keys, fee-rule ids or provider prices.
        options: payload.options.map(projectPublicOption) as never,
        as_of: asOf,
      } as never,
      { onConflict: "scenario_key" },
    );
    // Append-only public history so pages can show how pricing moved over time.
    // Derived strictly from the same anonymized options that were published.
    const point = summarizeHistoryPoint(payload.options);
    await supabaseAdmin.from("public_rate_history").upsert(
      {
        scenario_key: key,
        product_slug: "dscr",
        loan_purpose: payload.scenario.loanPurpose,
        as_of: asOf,
        low_rate: point.lowRate,
        par_rate: point.parRate,
        low_apr: point.lowApr,
        option_count: payload.options.length,
      } as never,
      { onConflict: "scenario_key,as_of", ignoreDuplicates: true },
    );
  } catch (err) {
    console.error("[rate-center] snapshot write failed", err instanceof Error ? err.message : err);
  }
}


/** Snapshot fallback used whenever a live call cannot be made or fails. */
async function fallbackOrEmpty(
  key: string,
  scenario: PublicRateScenario,
  message: string,
  correlationId: string,
): Promise<PublicRateResponse> {
  const snap = await readSnapshot(key, scenario);
  if (snap && usableAsFallback(snap.asOf)) {
    return {
      ...snap,
      correlationId,
      notices: [...new Set([...snap.notices, message])],
    };
  }
  return emptyRateResponse(scenario, message, {
    availability: checkStateAvailability(scenario.state),
    correlationId,
    notices: marketLikelyClosed() ? [MARKET_CLOSED_NOTE] : [],
  });
}

/**
 * Public, read-only: the latest stored anonymized snapshot for a scenario.
 *
 * Self-healing: when no displayable snapshot exists, the app reprices the
 * scenario itself instead of showing "unavailable" — but at most once per
 * scenario per UTC day, claimed through a database row, so page traffic can
 * never turn into repeated provider calls.
 */
export const getDscrRateSnapshot = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) =>
    z.object({ key: z.string().max(200).optional() }).parse(data ?? {}),
  )
  .handler(async ({ data }) => {
    const key = data.key ?? scenarioKey(DEFAULT_RATE_SCENARIO);
    const snap = await readSnapshot(key, DEFAULT_RATE_SCENARIO);
    if (snap) return snap;

    if (key === scenarioKey(DEFAULT_RATE_SCENARIO)) {
      try {
        const { tryClaimSelfHealSlot } = await import("./dscr-daily-refresh.server");
        if (await tryClaimSelfHealSlot(key)) {
          return (await priceDscrScenario({ data: DEFAULT_RATE_SCENARIO })) as PublicRateResponse;
        }
      } catch (err) {
        console.error(
          "[rate-center] snapshot self-heal failed",
          err instanceof Error ? err.message : err,
        );
      }
    }

    return emptyRateResponse(
      DEFAULT_RATE_SCENARIO,
      "Live pricing has not been published for this scenario yet. Price your own scenario below or request a written quote.",
      {
        availability: checkStateAvailability(DEFAULT_RATE_SCENARIO.state),
        notices: marketLikelyClosed() ? [MARKET_CLOSED_NOTE] : [],
      },
    );
  });


/** Admin switch: true = old direct Optimal Blue pricing; false = central engine first. */
async function useLegacyDscrPricing(): Promise<boolean> {
  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("site_settings")
      .select("dscr_use_legacy_pricing" as "id")
      .eq("id", 1)
      .maybeSingle();
    return (data as { dscr_use_legacy_pricing?: boolean } | null)?.dscr_use_legacy_pricing === true;
  } catch {
    return false;
  }
}

/** Central engine quotes in this site's shape, or null to fall back locally. */
async function tryCentralPricing(
  scenario: PublicRateScenario,
  correlationId: string,
): Promise<PublicRateResponse | null> {
  try {
    const { resolveCentralPrimaryConnection } = await import("./central-engine/config");
    const cfg = resolveCentralPrimaryConnection(process.env);
    if (cfg.mode !== "shadow") return null;
    const { fetchEngineDscrClient } = await import("./central-engine/adapter.server");
    const { readCentralDscrResponse } = await import("./central-engine/from-client-contract");
    const res = await fetchEngineDscrClient(cfg, scenario);
    if (!res.ok) {
      console.warn(`[rate-center][${correlationId}] central engine unavailable: ${res.error}`);
      return null;
    }
    const read = readCentralDscrResponse(res.payload, scenario);
    if (!read.ok) {
      console.warn(`[rate-center][${correlationId}] central payload rejected: ${read.reason}`);
      return null;
    }
    return { ...read.response, correlationId };
  } catch (err) {
    console.error(`[rate-center][${correlationId}] central engine error`, err instanceof Error ? err.message : err);
    return null;
  }
}

/**
 * Full (unredacted) pricing pipeline. Server-only: callers on public paths
 * must pass the result through `projectPublicRateResponse` first.
 */
async function priceDscrScenarioInternal(scenario: PublicRateScenario): Promise<PublicRateResponse> {
  const correlationId = newCorrelationId();
  const ltv = scenarioLtv(scenario);
  const key = scenarioKey(scenario);

  // 1) Where we can actually originate ------------------------------------
  const availability = checkStateAvailability(scenario.state);
  if (!availability.canShowPricing) {
    return emptyRateResponse(scenario, availability.message, { availability, correlationId });
  }

  // 2) Scenario guardrails, checked before we spend a provider call --------
  const gate = gateDscrScenario({
    ltv,
    fico: scenario.fico,
    dscr: scenario.dscr,
    loanAmount: scenario.loanAmount,
    propertyValue: scenario.propertyValue,
  });
  if (!gate.ok) {
    return emptyRateResponse(scenario, gate.message, { availability, correlationId });
  }

  // 3) Short cache ---------------------------------------------------------
  const cached = cache.get(key);
  if (cached && Date.now() - cached.at < FRESHNESS_CONFIG.publicCacheTtlMs) {
    return {
      ...cached.payload,
      source: "cache",
      correlationId,
      freshness: classifyFreshness(cached.payload.asOf, "cache"),
    };
  }

  // 4) Cost control --------------------------------------------------------
  const limited = rateLimited(clientIp()) || globalLimited();

  // 4b) Central pricing engine (main source unless the admin "use old
  //     pricing" switch is on). Fail-closed: any invalid/thin/expired
  //     payload falls through to the local Optimal Blue path below.
  if (!limited && !(await useLegacyDscrPricing())) {
    const central = await tryCentralPricing(scenario, correlationId);
    if (central) {
      cache.set(key, { at: Date.now(), payload: central });
      return central;
    }
  }

  if (limited) {
    return fallbackOrEmpty(
      key,
      scenario,
      "We're pricing a lot of scenarios right now. The most recent verified pricing is shown — reprice again shortly for a live quote.",
      correlationId,
    );
  }

  const { areCredentialsConfigured } = await import("./optimal-blue.server");
  if (!areCredentialsConfigured()) {
    return fallbackOrEmpty(key, scenario, UNAVAILABLE_COPY, correlationId);
  }

  // 5) Single-flight the provider call -------------------------------------
  const existing = inFlight.get(key);
  if (existing) return existing;

  const work = (async (): Promise<PublicRateResponse> => {
    const { runPricing } = await import("./optimal-blue-pricing.server");
    const { selectPublicOptionsWithCoverage } = await import("./rate-selection");
    const { obBusinessChannelId, obOriginatorId } = await import("./optimal-blue-config.server");

    let report;
    try {
      report = await runPricing({
        scenario: toProviderScenario(scenario),
        channel: "public",
        persist: true,
        // Detail every distinct product we can; the runner fans out
        // round-robin by investor so no lender loses par/fee detail.
        detailProductLimit: 12,
      });
    } catch (err) {
      // Provider detail never reaches the browser.
      console.error(
        `[rate-center][${correlationId}] pricing failed`,
        err instanceof Error ? err.message : err,
      );
      return fallbackOrEmpty(key, scenario, UNAVAILABLE_COPY, correlationId);
    }

    // Each published option gets an opaque token. The token maps, server
    // side only, to the internal investor/product metadata for that exact
    // quote so a "Start My Approval" click carries the real pricing context
    // into the lead without ever exposing lender identity publicly.
    const tokenRows: Array<Record<string, unknown>> = [];
    const { options, coverage } = selectPublicOptionsWithCoverage(report.quotes, {
      fallbackProgramLabel: fallbackProgramLabel(scenario),
      scenarioTags: scenarioTags(scenario),
      // Every eligible investor is represented; each investor contributes up
      // to three useful comparison options instead of its whole grid.
      limit: 24,
      perInvestorLimit: 3,
      // Single lock period per comparison table. The client always starts on
      // 30 days and only sees rows carrying the lock they selected.
      lockPeriodDays: scenario.lockPeriodDays,
      baseLoanAmount: scenario.loanAmount,
      // HARD PRODUCTION RULE: a public quote must be a feesIn=Yes quote
      // priced under the production Loan Officer Pricing context, at the
      // exact lock period being displayed. Anything else stays internal.
      requireFeesInYes: true,
      businessChannelId: obBusinessChannelId(),
      originatorId: obOriginatorId(),
      tokenFor: (q, i, lenderLabel) => {
        const token = `${report.correlationId ?? correlationId}-${i}-${newCorrelationId()}`;
        tokenRows.push({
          token,
          correlation_id: report.correlationId ?? correlationId,
          search_id: q.searchId,
          product_id: q.productId,
          product_name: q.productName,
          investor_id: q.investorId,
          investor_name: q.investorName,
          fees_in_basis: q.feesInBasis,
          rate: q.rate,
          apr: q.apr,
          price: q.price,
          discount_dollar: q.discountDollar,
          discount_percent: q.discountPercent,
          rebate_dollar: q.rebateDollar,
          rebate_percent: q.rebatePercent,
          business_channel_id: q.businessChannelId,
          originator_id: q.originatorId,
          principal_and_interest: q.principalAndInterest,
          lock_period: q.lockPeriod,
          lender_label: lenderLabel,
          scenario: scenario as never,
        });
        return token;
      },
    });

    // Coverage diagnostic — internal only. Proves which investors OB
    // returned, which are displayed, and the exact reason for any exclusion.
    console.info(
      `[rate-center][${correlationId}] coverage`,
      JSON.stringify({
        scenarioKey: key,
        feesInPrimary: report.feesInPrimary,
        feesInFallbackUsed: report.feesInFallbackUsed,
        providerInvestors: report.providerCoverage.map((c) => ({
          investor: c.investorName,
          products: c.products.length,
          rateRows: c.rateRows,
          feesIn: c.feesInBasis,
        })),
        investorsReturned: coverage.investorsReturned,
        investorsDisplayed: coverage.investorsDisplayed,
        investorsExcluded: coverage.investorsExcluded,
        rowsReturned: coverage.rowsReturned,
        rowsEligible: coverage.rowsEligible,
        rowsDisplayed: coverage.rowsDisplayed,
        // DSCR product eligibility gate: raw vs public-eligible vs blocked.
        rowsRawReturned: coverage.rowsRawReturned,
        rowsDscrEligible: coverage.rowsDscrEligible,
        rowsBlockedNotDscr: coverage.rowsBlockedNotDscr,
        blockedProducts: coverage.blockedProducts,
        investorsAfterDscrGate: coverage.investors
          .filter((i) => i.displayed)
          .map((i) => i.investorName),
        fallbackInvestors: coverage.fallbackInvestors,
        feesInBlockedInvestors: coverage.feesInBlockedInvestors,
        lockPeriodRequested: coverage.lockPeriodRequested,
        availableLockPeriods: coverage.availableLockPeriods,
        rowsFilteredByLock: coverage.rowsFilteredByLock,
        rowsFailingReconciliation: coverage.rowsFailingReconciliation,
        reconciliationFailures: coverage.reconciliationFailures,
        excluded: coverage.investors
          .filter((i) => !i.displayed)
          .map((i) => ({ investor: i.investorName, reason: i.exclusionReason, detail: i.exclusionDetail })),
      }),
    );


    // Borrower-facing lender fees come from OUR internal fee schedules,
    // never from the pricing engine's fee endpoint. The engine only tells us
    // which investor priced the quote; that maps to the fee schedule.
    try {
      const { estimateLenderFees } = await import("./lender-fee-schedules.server");
      const feeCtx = {
        programFamily:
          scenario.citizenship === "ForeignNational" ? ("ForeignNational" as const) : ("DSCR" as const),
        state: scenario.state,
        loanAmount: scenario.loanAmount,
        entityVesting: scenario.entityVesting,
        trustVesting: false,
        shortTermRental: scenario.shortTermRental,
        borrowerCount: 1,
        propertyType: scenario.propertyType,
        loanPurpose: scenario.loanPurpose,
      };
      const { resolvePrimaryLenderFee, PRIMARY_FEE_TOLERANCE } = await import("./primary-lender-fee");
      // Central, versioned fee configuration. Empty map = code schedules
      // apply unchanged, which is also the failure mode.
      const { loadFeeConfig } = await import("./fee-config.server");
      const { applyFeeConfigOverride } = await import("./fee-config");
      const feeConfig = await loadFeeConfig({
        mortgageProduct: scenario.citizenship === "ForeignNational" ? "foreign_national" : "dscr",
      });
      for (let i = 0; i < options.length; i++) {
        const o = options[i];
        const row = tokenRows[i];
        if (!o) continue;
        // Some rows come back without an investor name; the internal product
        // name still carries the wholesale lender, so it is the fallback.
        const investorHint =
          ((row?.investor_name as string | null) ?? null) ||
          ((row?.product_name as string | null) ?? null);
        const est = estimateLenderFees(
          investorHint,
          feeCtx,
          (row?.product_name as string | null) ?? null,
        );
        o.lenderFeeEstimate = est;
        // Headline fee column: the investor's primary underwriting/admin fee
        // ONLY. Ancillary schedule charges stay in the secondary section and
        // are never rolled into points, credit or a combined total.
        const feesInBasis = (row?.fees_in_basis as "Yes" | "No" | null) ?? null;
        const resolved = resolvePrimaryLenderFee(est, feesInBasis);
        let primary = resolved;
        if (resolved) {
          const overridden = applyFeeConfigOverride(
            { ...resolved, id: resolved.feeId },
            resolved.lenderKey,
            feeConfig,
          );
          if (overridden.feeAmountSource === "central_config") {
            // A centrally configured amount that no longer matches the live
            // fees-in price observation may not claim to be in pricing, and
            // may not act as an APR finance charge.
            const ref = resolved.obReferenceAmount;
            const withinTolerance =
              ref == null || Math.abs((overridden.amount ?? 0) - ref) <= PRIMARY_FEE_TOLERANCE;
            primary = {
              ...resolved,
              label: overridden.label,
              amount: overridden.amount,
              reflectedInPricing: resolved.reflectedInPricing && withinTolerance,
              verifiedForApr: overridden.verifiedForApr && withinTolerance,
              reviewFlag: resolved.reviewFlag || !withinTolerance,
            };
          }
        }
        o.primaryLenderFee = primary;
        o.lenderFeeLines = primary
          ? [{ label: primary.label, amount: primary.amount ?? 0 }]
          : [];
        o.totalUpfrontLenderCharges = primary?.amount ?? null;

        if (primary && primary.matchStatus !== "validated") {
          console.warn(
            `[rate-center][${correlationId}] primary lender fee not validated`,
            JSON.stringify({
              lender: o.lenderLabel,
              feesInBasis,
              matchStatus: primary.matchStatus,
              variance: primary.varianceDollars,
            }),
          );
        }
      }

    } catch (err) {
      console.error(
        `[rate-center][${correlationId}] lender fee estimate failed`,
        err instanceof Error ? err.message : err,
      );
    }

    // APR engine. The pricing engine's APR is retained verbatim on `obApr`;
    // `apr` becomes the APR we can actually defend for this quote. See
    // src/lib/apr-engine.ts for the double-count rules (fees-in fee already
    // in price, lender-paid comp never a borrower finance charge).
    {
      const { computeQuoteApr, termMonthsFor } = await import("./apr-engine");
      const months = termMonthsFor(scenario.loanTerm);
      for (const o of options) {
        const result = computeQuoteApr({
          rate: o.rate,
          obApr: o.apr,
          loanAmount: scenario.loanAmount,
          pointsDollar: o.pointsDollar,
          creditDollar: o.creditDollar,
          principalAndInterest: o.principalAndInterest,
          termMonths: months,
          interestOnly: o.interestOnly,
          amortizationType: scenario.amortizationType,
          feesInBasis: o.feesIncludedInPrice == null ? null : o.feesIncludedInPrice ? "Yes" : "No",
          primaryFee: o.primaryLenderFee
            ? {
                label: o.primaryLenderFee.label,
                amount: o.primaryLenderFee.amount,
                // Only a schedule-verified or price-validated amount may act
                // as a finance charge. Anything else keeps the engine APR.
                validated: o.primaryLenderFee.verifiedForApr,
              }
            : null,
        });
        o.obApr = result.obApr;
        o.apr = result.aprDisplay;
        o.aprSource = result.aprSource;
        o.aprBasis = result.aprBasis;
        o.aprAuditStatus = result.aprAuditStatus;
        o.financeChargesIncluded = result.financeChargesIncluded;
        o.financeChargesExcluded = result.financeChargesExcluded;
        o.divergenceFromObApr = result.divergenceFromObApr;
        if (result.aprAuditStatus === "review") {
          console.warn(
            `[rate-center][${correlationId}] APR engine review`,
            JSON.stringify({
              lender: o.lenderLabel,
              rate: o.rate,
              obApr: result.obApr,
              aprDisplay: result.aprDisplay,
              basis: result.aprBasis,
              notes: result.auditNotes,
            }),
          );
        }
      }
    }

    // APR provenance: log any published row whose APR was flagged for review
    // so staff can verify it against the lender's own pricing record.
    for (const o of options) {
      if (o.aprAudit && o.aprAudit.status === "review") {
        console.warn(
          `[rate-center][${correlationId}] APR flagged for review`,
          JSON.stringify({
            lender: o.lenderLabel,
            rate: o.rate,
            apr: o.apr,
            obApr: o.obApr,
            spreadBps: o.aprAudit.spreadBps,
            issues: o.aprAudit.issues,
          }),
        );
      }
    }


    // APR / fee integrity diagnostic. Purely internal — the displayed APR is
    // never altered. This surfaces quotes whose APR cannot reconcile with the
    // pricing shown beside it (e.g. APR == note rate while a primary lender
    // fee is embedded in the price via feesIn=Yes).
    {
      const { detectAprIntegrity, summarizeAprIntegrity } = await import("./apr-integrity");
      const rows = options.map((o) => ({
        lenderLabel: o.lenderLabel,
        findings: detectAprIntegrity({
          rate: o.rate,
          apr: o.apr,
          primaryFeeAmount: o.primaryLenderFee?.amount ?? null,
          pointsDollar: o.pointsDollar ?? null,
        }),
      }));
      const summary = summarizeAprIntegrity(rows);
      if (summary.flagged) {
        console.warn(
          `[rate-center][${correlationId}] APR integrity findings`,
          JSON.stringify({
            ...summary,
            rows: rows
              .filter((r) => r.findings.length)
              .map((r) => ({ lender: r.lenderLabel, codes: r.findings.map((f) => f.code) })),
          }),
        );
      }
    }


    if (tokenRows.length) {

      try {
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        for (let i = 0; i < tokenRows.length; i++) {
          const o = options[i];
          if (!o) continue;
          Object.assign(tokenRows[i]!, {
            program_label: o.programLabel,
            points_percent: o.pointsPercent,
            points_dollar: o.pointsDollar,
            credit_percent: o.creditPercent,
            credit_dollar: o.creditDollar,
            lock_period: o.lockPeriod,

            // Audit snapshot: exactly the fee rules, primary fee treatment
            // and the APR verdict the borrower was shown, so later changes
            // never rewrite history.
            scenario: {
              ...scenario,
              feeSnapshot: o.lenderFeeEstimate ?? null,
              primaryFeeSnapshot: o.primaryLenderFee ?? null,
              feesInBasis: o.feesIncludedInPrice == null ? null : o.feesIncludedInPrice ? "Yes" : "No",
              aprAudit: o.aprAudit ?? null,
              aprSnapshot: {
                obApr: o.obApr ?? null,
                aprDisplay: o.apr,
                aprSource: o.aprSource ?? null,
                aprBasis: o.aprBasis ?? null,
                aprAuditStatus: o.aprAuditStatus ?? null,
                financeChargesIncluded: o.financeChargesIncluded ?? null,
                financeChargesExcluded: o.financeChargesExcluded ?? null,
                divergenceFromObApr: o.divergenceFromObApr ?? null,
              },
              pricingAudit: o.pricingAudit ?? null,
              lockPeriodRequested: scenario.lockPeriodDays,

              // Advertising audit trail metadata. Retained for two years so
              // any publicly displayed quote can be reproduced exactly as
              // shown, including the disclosure wording in force that day.
              audit: {
                sourceSite: "dscr-investor-site",
                engine: "optimal_blue",
                mortgageProduct:
                  scenario.citizenship === "ForeignNational" ? "foreign_national" : "dscr",
                disclosureVersion: RATE_DISCLOSURE_VERSION,
                displayedAt: new Date().toISOString(),
                retainUntil: new Date(Date.now() + 2 * 365 * 24 * 60 * 60 * 1000).toISOString(),
                feeAmountSource: o.primaryLenderFee?.feeId ?? null,
              },


            } as never,

          });
        }
        await supabaseAdmin.from("rate_quote_selections").insert(tokenRows as never);
      } catch (err) {
        console.error(
          `[rate-center][${correlationId}] quote token persist failed`,
          err instanceof Error ? err.message : err,
        );
      }
    }

    if (!options.length) {
      const failed = report.endpoints.some((e) => !e.ok);
      return fallbackOrEmpty(
        key,
        scenario,
        failed
          ? "No eligible programs came back for that scenario. Try a lower LTV, a higher DSCR, or a different property type."
          : "No eligible programs matched those inputs. Adjust the loan amount, DSCR, credit score, or lock period and reprice.",
        correlationId,
      );
    }

    const asOf = new Date().toISOString();
    const notices: string[] = [];
    if (availability.message) notices.push(availability.message);
    if (report.guidelineFlags.length) {
      notices.push(
        "This scenario sits outside our standard investor parameters, so it is priced as an exception and requires review.",
      );
    }

    const payload: PublicRateResponse = {
      ok: true,
      source: "live",
      asOf,
      scenario,
      ltv,
      options,
      providerParQuotes: report.parQuotes ?? [],
      availableLockPeriods: coverage.availableLockPeriods,
      message: null,
      freshness: classifyFreshness(asOf, "live"),
      availability,
      feeDataStatus: aggregateFeeStatus(options),
      correlationId: report.correlationId,
      notices,
    };

    cache.set(key, { at: Date.now(), payload });
    await writeSnapshot(key, payload);
    return payload;
  })().finally(() => inFlight.delete(key));

  inFlight.set(key, work);
  return work;
}

/** Public: prices a validated scenario live and returns anonymized options. */
export const priceDscrScenario = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => {
    // Business-purpose engine only: an owner-occupied / second-home request
    // is rejected outright rather than being silently priced as investment.
    assertInvestmentOccupancy((data as { occupancy?: unknown } | null)?.occupancy);
    return scenarioSchema.parse(data ?? {});
  })

  .handler(async ({ data }): Promise<PublicRateResponse> =>
    // Every return path (live, cache, snapshot fallback, empty) is projected
    // here, so no wholesale lender key or provider price reaches the browser.
    projectPublicRateResponse(await priceDscrScenarioInternal(data as PublicRateScenario)),
  );

// ---------------------------------------------------------------------------
// Internal staff service (CRM-ready)
// ---------------------------------------------------------------------------

async function assertStaff(context: { supabase: any; userId: string }) {
  const { data: isAdmin } = await context.supabase.rpc("has_role", {
    _user_id: context.userId,
    _role: "admin",
  });
  const { data: isLo } = await context.supabase.rpc("has_role", {
    _user_id: context.userId,
    _role: "loan_officer",
  });
  if (!isAdmin && !isLo) throw new Error("Forbidden");
}

const staffSchema = scenarioSchema.extend({
  leadId: z.string().uuid().nullable().optional(),
  contactId: z.string().max(64).nullable().optional(),
  loanId: z.string().max(64).nullable().optional(),
});

/**
 * Authenticated staff quoting service. Returns the FULL internal normalized
 * quotes (real product/investor names and provider IDs) and persists the run
 * to pricing_searches / pricing_quotes for CRM use.
 */
export const runStaffDscrQuote = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => staffSchema.parse(data ?? {}))
  .handler(async ({ context, data }) => {
    await assertStaff(context as any);

    const { areCredentialsConfigured } = await import("./optimal-blue.server");
    if (!areCredentialsConfigured()) {
      return { ok: false as const, message: "Optimal Blue credentials are not configured." };
    }

    const { runPricing } = await import("./optimal-blue-pricing.server");
    const { selectPublicOptions } = await import("./rate-selection");

    const scenario = data as unknown as PublicRateScenario;
    const report = await runPricing({
      scenario: toProviderScenario(scenario),
      channel: "agent",
      createdBy: (context as any).userId ?? null,
      leadId: data.leadId ?? null,
      contactId: data.contactId ?? null,
      loanId: data.loanId ?? null,
      persist: true,
      detailProductLimit: 5,
    });

    return {
      ok: true as const,
      correlationId: report.correlationId,
      searchId: report.searchId,
      ltv: report.ltv,
      quoteCount: report.quoteCount,
      exactParAvailable: report.exactParAvailable,
      parQuotes: report.parQuotes,
      lenderFeesAvailable: report.lenderFeesAvailable,
      lenderFeesNote: report.lenderFeesNote,
      lenderFeesByProduct: report.lenderFeesByProduct,
      guidelineFlags: report.guidelineFlags,
      persistedSearchRowId: report.persistedSearchRowId,
      persistError: report.persistError,
      endpoints: report.endpoints,
      totalMs: report.totalMs,
      /** Full internal detail — staff only. */
      quotes: report.quotes,
      /** What the borrower would see if this were published. */
      publicPreview: selectPublicOptions(report.quotes, {
        fallbackProgramLabel: fallbackProgramLabel(scenario),
        scenarioTags: scenarioTags(scenario),
        limit: 6,
      }),
    };
  });
